import { useId } from 'react';
import { Box, InputAdornment, OutlinedInput, useTheme } from '@mui/material';

interface LoginFieldProps {
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  /** Slot for trailing content, e.g. a "Forgot?" button. */
  endAdornment?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  name?: string;
}

/**
 * Labelled input matching the prototype LoginField:
 * uppercase caption label above, outlined MUI input below.
 * Focus ring colour is driven by `primary.main` (Indigo).
 */
export function LoginField({
  label,
  type = 'text',
  value,
  onChange,
  autoComplete,
  endAdornment,
  disabled,
  required,
  name,
}: LoginFieldProps) {
  const id = useId();
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box
        component="label"
        htmlFor={id}
        sx={{
          fontSize: '0.71875rem', // caption — 11.5px
          fontWeight: 500,
          color: 'text.disabled', // ink-3
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          userSelect: 'none',
          cursor: 'default',
        }}
      >
        {label}
      </Box>

      <OutlinedInput
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        disabled={disabled}
        required={required}
        name={name}
        notched={false}
        label={undefined}
        endAdornment={
          endAdornment ? (
            <InputAdornment position="end">{endAdornment}</InputAdornment>
          ) : undefined
        }
        sx={{
          fontSize: '0.875rem', // body1 — 14px
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.divider,
            transition: 'border-color 0.15s',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.text.disabled,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
        }}
      />
    </Box>
  );
}
